/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { reducer, union } from "ts-action";
import { on } from "ts-action-immer";
import { DataReducerState } from "../types";
import {
  addNewDAndPTemplateItem,
  deleteDAndPTemplateItem,
  isFetchingModel,
  receiveTemplateBuilder,
  receiveTemplateBuilderModels,
  revalidateTemplateBuilder,
  updateBothTemplateItems,
  updateDataTemplate,
  updateDataTemplateItem,
  updateDataTemplateItems,
  updateDetailTemplates,
  updateDetailTemplatesOrder,
  updatePresentationTemplate,
  updatePresentationTemplateItem,
  updatePresentationTemplateItems,
  updateTemplateBuilder,
} from "../actionCreators";
import dataTemplateReducer from "./dataTemplateReducer";
import presentationTemplateReducer from "./presentationTemplateReducer";
import {
  addNewTemplateItem,
  deleteTemplateItem,
  updateTemplateItems,
} from "../utils";
import { AllTemplateBuilderModels } from "../../../requests/templateBuilder/types";
import typeGuards from "../../../type-guards";

const initialState: DataReducerState = {
  data: undefined,
  isFetching: false,
};

const dataReducer = reducer(
  initialState,
  on(receiveTemplateBuilder, (state, receiveAction) => {
    state.data = {
      ...receiveAction.payload.templateBuilder,
      dataTemplate: dataTemplateReducer(
        state?.data?.dataTemplate,
        receiveAction
      )!,
      presentationTemplate: presentationTemplateReducer(
        state?.data?.presentationTemplate,
        receiveAction
      ),
    } as AllTemplateBuilderModels; // Tady třeba castovat, protože jinak neví, že spolu sedí dataTempalte, presentationTemplate a zbytek dat
  }),
  on(
    ...union(updateTemplateBuilder, revalidateTemplateBuilder),
    (state, { payload: { updatedTemplateBuilder } }) => {
      state.data = updatedTemplateBuilder;
    }
  ),
  on(
    ...union(
      updateDataTemplate,
      updateDataTemplateItem,
      updateDataTemplateItems,
      updateDetailTemplates
    ),
    (state, updateDAction) => {
      if (!state?.data?.dataTemplate) return;
      state.data.dataTemplate = dataTemplateReducer(
        state.data.dataTemplate,
        updateDAction
      )!;
    }
  ),
  on(
    ...union(
      updatePresentationTemplate,
      updatePresentationTemplateItems,
      updatePresentationTemplateItem,
      updateDetailTemplatesOrder
    ),
    (state, updatePAction) => {
      if (!state.data) return;
      state.data.presentationTemplate = presentationTemplateReducer(
        state.data.presentationTemplate,
        updatePAction
      );
    }
  ),
  on(receiveTemplateBuilderModels, (state, { payload: { models } }) => {
    if (state.data) {
      state.data.dataTemplate = models.dataTemplate;
      state.data.presentationTemplate = models.presentationTemplate;
    }
  }),
  on(updateBothTemplateItems, (state, { payload: { item } }) => {
    const { data } = state;
    if (!data) return;
    const { hasStandardDataModel, hasPresentationModel } =
      typeGuards.forTBuilderModel;

    if (!hasStandardDataModel(data) || !hasPresentationModel(data)) {
      return;
    }

    const { newItems, newPItems, idChanged, targetJoinId } =
      updateTemplateItems(
        item,
        data.dataTemplate.items,
        data.presentationTemplate.items,
        data.emptyDataTemplateItem,
        data.tables,
        data.dataTemplate.idCounter,
        data.type,
        data.system
      );

    if (newPItems && newItems) {
      if (idChanged) {
        data.dataTemplate.idCounter += 1;
      }
      data.dataTemplate.items = newItems;
      data.presentationTemplate.items = newPItems;

      if (data.presentationTemplate.mobileRowLayout.layout) {
        data.presentationTemplate.mobileRowLayout.layout = Object.fromEntries(
          Object.entries(data.presentationTemplate.mobileRowLayout.layout) // I <3 PRETTIER
            .filter(([k, val]) => val.columnValueIndex !== targetJoinId)
        );
      }
    }
  }),
  on(addNewDAndPTemplateItem, (state) => {
    const { data } = state;
    if (!data) return;

    const { hasStandardDataModel, hasPresentationModel } =
      typeGuards.forTBuilderModel;

    if (!hasStandardDataModel(data) || !hasPresentationModel(data)) {
      return;
    }

    const { newItems, newPItems } = addNewTemplateItem(
      data.dataTemplate.items,
      data.presentationTemplate.items,
      data.emptyDataTemplateItem,
      data.tables,
      data.dataTemplate.idCounter,
      data.type,
      data.system
    );
    data.dataTemplate.items = newItems;
    data.presentationTemplate.items = newPItems;
    data.dataTemplate.idCounter += 1;
  }),
  on(deleteDAndPTemplateItem, (state, { payload: { itemId } }) => {
    const { data } = state;

    if (!data) return;
    const { hasStandardDataModel, hasPresentationModel } =
      typeGuards.forTBuilderModel;

    if (!hasStandardDataModel(data) || !hasPresentationModel(data)) {
      return;
    }

    const { newItems, newPItems, newLayout } = deleteTemplateItem(
      itemId,
      data.dataTemplate.items,
      data.presentationTemplate.items,
      data.presentationTemplate.mobileRowLayout
    );
    data.dataTemplate.items = newItems;
    data.presentationTemplate.items = newPItems;
    data.presentationTemplate.mobileRowLayout = newLayout;
  }),
  on(isFetchingModel, (state, { payload: { isFetching } }) => {
    state.isFetching = isFetching;
  })
);

export default dataReducer;
export const dataReducerInitialState = initialState;
